// Atlas aeroway raster (AVI-1). PURE + deterministic (GEN-1): runs inside the
// Atlas generator after the DEM resample (ELV-3) and pins runway/taxiway columns
// to a single flat Y so they are landable, painting a dashed white centerline +
// threshold bars (runways) or a yellow centerline (taxiways); aprons/helipads fill
// their ring at the local ground. Inert when the city has no aeroways → a
// non-aviation Atlas world is byte-identical (parity).
import { RUNWAY } from '../config';
import {
  boundsOf, bboxIntersects, pointInPolygon, type Aeroway, type BBox, type Pt,
} from './intermediate';

/** Block-state ids the aeroway raster paints. */
export interface AerowayIds {
  runway: number;    // pavement (light_gray_concrete)
  paint: number;     // runway centerline + threshold bars (white_concrete)
  taxiway: number;   // taxiway pavement (gray_concrete)
  taxiPaint: number; // taxiway centerline (yellow_concrete)
  apron: number;     // apron / helipad (gray_concrete)
}

/** What a column on an aeroway becomes: a surface block + the flat Y it is pinned
 *  to (the single-y flatten, AVI-1). `null` → the column is not on any aeroway. */
export type AerowayHit = { surface: number; flatY: number } | null;

interface Strip {
  runway: boolean;
  half: number;      // pavement half-width, blocks
  flatY: number;     // the single flatten Y (mean ground under the strip)
  verts: Pt[];       // centerline vertices
  cum: number[];     // cumulative arc-length at each vertex
  length: number;
  bb: BBox;
}

interface Pad {
  ring: Pt[];
  flatY: number;
  bb: BBox;
}

/** Nearest point on segment (ax,az)-(bx,bz) to (px,pz): its clamped param t and the
 *  squared perpendicular distance. */
function projectSeg(px: number, pz: number, ax: number, az: number, bx: number, bz: number): { t: number; d2: number } {
  const dx = bx - ax, dz = bz - az;
  const len2 = dx * dx + dz * dz;
  const t = len2 === 0 ? 0 : Math.max(0, Math.min(1, ((px - ax) * dx + (pz - az) * dz) / len2));
  const cx = ax + t * dx, cz = az + t * dz;
  return { t, d2: (px - cx) ** 2 + (pz - cz) ** 2 };
}

/** Pinned-and-painted runways/taxiways/aprons for one Atlas city (AVI-1). */
export class AerowayField {
  private readonly strips: Strip[] = [];
  private readonly pads: Pad[] = [];

  constructor(aeroways: readonly Aeroway[], scale: number, groundYAt: (wx: number, wz: number) => number) {
    for (const a of aeroways) {
      if (a.kind === 'apron' || a.kind === 'helipad') {
        const ring = a.points.map(([x, z]) => [x, z] as Pt);
        if (ring.length < 3) continue;
        const bb = boundsOf(ring);
        const flatY = Math.round(groundYAt((bb.minX + bb.maxX) / 2, (bb.minZ + bb.maxZ) / 2));
        this.pads.push({ ring, flatY, bb });
        continue;
      }
      const runway = a.kind === 'runway';
      const widthM = a.widthM ?? (runway ? RUNWAY.WIDTH_DEFAULT_M : RUNWAY.TAXIWAY_WIDTH_M);
      const half = Math.max(2, Math.round(widthM / scale / 2));
      const verts = a.points.map(([x, z]) => [x, z] as Pt);
      // Cumulative arc-length + a flatten Y = the mean ground sampled along the
      // centerline (so the flat strip balances cut against fill, AVI-1).
      const cum: number[] = [0];
      let length = 0, groundSum = 0, groundN = 0;
      for (let i = 0; i < verts.length; i++) {
        const [vx, vz] = verts[i]!;
        groundSum += groundYAt(vx, vz); groundN++;
        if (i > 0) {
          const [px, pz] = verts[i - 1]!;
          length += Math.hypot(vx - px, vz - pz);
          cum.push(length);
        }
      }
      this.strips.push({ runway, half, flatY: Math.round(groundSum / Math.max(1, groundN)), verts, cum, length, bb: boundsOf(verts) });
    }
  }

  /** True when there is nothing to raster (parity: the generator skips us). */
  get empty(): boolean { return this.strips.length === 0 && this.pads.length === 0; }

  /** Padded chunk-filter bbox test (canopy of the widest strip). */
  intersects(pad: BBox): boolean {
    for (const s of this.strips) if (bboxIntersects({ minX: s.bb.minX - s.half - 1, minZ: s.bb.minZ - s.half - 1, maxX: s.bb.maxX + s.half + 1, maxZ: s.bb.maxZ + s.half + 1 }, pad)) return true;
    for (const p of this.pads) if (bboxIntersects(p.bb, pad)) return true;
    return false;
  }

  /** The aeroway surface + flatten at a world column, or null if off every strip. */
  surfaceAt(wx: number, wz: number, ids: AerowayIds): AerowayHit {
    const cx = wx + 0.5, cz = wz + 0.5;
    for (const s of this.strips) {
      // Nearest segment: perpendicular distance + arc-length position `along`.
      let bestD2 = Infinity, along = 0;
      for (let i = 0; i + 1 < s.verts.length; i++) {
        const [ax, az] = s.verts[i]!, [bx, bz] = s.verts[i + 1]!;
        const { t, d2 } = projectSeg(cx, cz, ax, az, bx, bz);
        if (d2 < bestD2) { bestD2 = d2; along = s.cum[i]! + t * (s.cum[i + 1]! - s.cum[i]!); }
      }
      if (bestD2 > s.half * s.half) continue; // off this strip
      const d = Math.sqrt(bestD2);
      if (s.runway) {
        const inThreshold = along <= RUNWAY.THRESHOLD_BLOCKS || along >= s.length - RUNWAY.THRESHOLD_BLOCKS;
        // Threshold bars: white piano keys straddling the centerline near each end.
        if (inThreshold && d <= s.half - 1 && d >= 0.5) return { surface: ids.paint, flatY: s.flatY };
        // Dashed white centerline down the middle.
        if (!inThreshold && d <= 0.6 && Math.floor(along / RUNWAY.CENTERLINE_DASH) % 2 === 0) return { surface: ids.paint, flatY: s.flatY };
        return { surface: ids.runway, flatY: s.flatY };
      }
      // Taxiway: yellow centerline on gray pavement.
      if (d <= 0.6) return { surface: ids.taxiPaint, flatY: s.flatY };
      return { surface: ids.taxiway, flatY: s.flatY };
    }
    for (const p of this.pads) if (pointInPolygon(cx, cz, p.ring)) return { surface: ids.apron, flatY: p.flatY };
    return null;
  }
}

/** Synthesize a centred sandbox airstrip for an aviation world that has no OSM
 *  aeroway (AVI-1 fallback) — a KHFD-oriented (≈020°/200°) runway through the world
 *  origin so you can always take off. Real OSM runways always take precedence.
 *  Documented in docs/decisions.md (M32). */
export function synthRunway(): Aeroway {
  const half = RUNWAY.SYNTH_LENGTH / 2;
  const th = (RUNWAY.SYNTH_BEARING_DEG * Math.PI) / 180;
  const dx = Math.sin(th), dz = -Math.cos(th); // +x east, +z south; bearing cw from north
  return {
    kind: 'runway',
    ref: '2/20',
    widthM: RUNWAY.SYNTH_WIDTH_M,
    points: [
      [Math.round(-dx * half), Math.round(-dz * half)],
      [Math.round(dx * half), Math.round(dz * half)],
    ],
  };
}
